import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMount } from "app/custom-hooks/useMount";
import { SWReactTableOptimizedWithSelection } from "app/components/React/Table/SWReactTableWrapperWithSelection";
import { CompetitiveLandscapeTableHeader } from "../TableHeader/CompetitiveLandscapeTableHeader";
import { MAX_DOMAINS_IN_CATEGORY } from "app/components/customCategoriesWizard/custom-categories-max-domains";
import { selectCompetitiveResults, selectIsFetchingResults } from "../../store/selectors";
import { useCompetitiveColumns } from "../../hooks/useCompetitiveColumns";
import { COMPETITIVE_LANDSCAPE_TABLE_KEY, COMPETITIVE_LANDSCAPE_TABLE_PROPERTY } from "../../configuration/constants";
import { fetchCompetitiveResultsThunk } from "../../store/effects";
import useCompetitiveQueryParams from "../../hooks/useCompetitiveQueryParams";
import { StyledCompetitiveResultsContainer } from "./styles";
import { getTrialMockedRows } from "app/pages/sales-intelligence/common-components/search-results-table";
import { CompetitiveSWReactTableWrapper } from "../CompetitiveSWReactTableWrapper/CompetitiveSWReactTableWrapper";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { EAvailabilityMode } from "app/common/services/pageClaims";
import type { CompetitiveLandscapeResults, CompetitiveLandscapeResultsEntry } from "../../types/dto";
import { useGetTrialTableHookProps } from "../../hooks/useGetTrialTableHookProps";

const ROW_HEIGHT = 41;

const CompetitiveLandscapePage = () => {
    const dispatch = useDispatch();

    const isFeatureHook = salesSettingsHelper.getWebsiteCompetitorsFeature() === EAvailabilityMode.HOOK;
    const hookLimit = salesSettingsHelper.getNumCompetitorsHookLimit();

    const { params } = useCompetitiveQueryParams();

    useMount(() => {
        dispatch(fetchCompetitiveResultsThunk(params));
    });

    const rawTableData = useSelector(selectCompetitiveResults);
    const isLoadingData = useSelector(selectIsFetchingResults);

    const isTrial = isFeatureHook && !isLoadingData && rawTableData?.Data?.length > hookLimit;

    const { rowsCount: mockedRowsCount } = useGetTrialTableHookProps(ROW_HEIGHT);

    const tableData = useMemo((): CompetitiveLandscapeResults => {
        if (!isTrial) {
            return rawTableData;
        }

        const { Data } = rawTableData;

        return {
            ...rawTableData,
            Data: [
                ...Data.slice(0, hookLimit),
                ...getTrialMockedRows<CompetitiveLandscapeResultsEntry>(Data, mockedRowsCount),
            ],
        };
    }, [rawTableData, isTrial, hookLimit, mockedRowsCount]);

    const { columns, onClickToggleColumns, onSort } = useCompetitiveColumns(isTrial);

    return (
        <StyledCompetitiveResultsContainer>
            <CompetitiveLandscapeTableHeader
                isTrial={isTrial}
                onClickToggleColumns={onClickToggleColumns}
                tableColumns={columns}
            />
            <CompetitiveSWReactTableWrapper
                isTrial={isTrial}
                rowHeight={ROW_HEIGHT}
            >
                <SWReactTableOptimizedWithSelection
                    isLoading={isLoadingData}
                    tableSelectionKey={COMPETITIVE_LANDSCAPE_TABLE_KEY}
                    tableSelectionProperty={COMPETITIVE_LANDSCAPE_TABLE_PROPERTY}
                    maxSelectedRows={MAX_DOMAINS_IN_CATEGORY}
                    cleanOnUnMount={true}
                    tableOptions={{
                        metric: COMPETITIVE_LANDSCAPE_TABLE_KEY,
                        tableSelectionTrackingParam: COMPETITIVE_LANDSCAPE_TABLE_PROPERTY,
                        trackName: "Competitive Landscape",
                    }}
                    tableData={tableData}
                    tableColumns={columns}
                    initialFilters={params}
                    onSort={onSort}
                />
            </CompetitiveSWReactTableWrapper>
        </StyledCompetitiveResultsContainer>
    );
};

export default CompetitiveLandscapePage;
